/**
 * AppIcon.tsx — App 识别图标（色来自 app.color，占位字来自 app.icon）
 *
 * 逐字搬自原型 design/Novel-admin/src/primitives.jsx 的 AppIcon + APP_ICON_SIZES：
 * - size 档位收口 sm/md/lg；也接受 {box,font,radius} 自定义对象（对齐蓝本 typeof size === 'object' 分支）；
 * - 背景优先级 color > app.color > var(--accent)；占位字 label ?? app.icon ?? ''；
 * - 复用全局 .app-icon 原子类（提供 color:white / 居中 / mono / 粗体），尺寸·背景·圆角经 inline style 覆盖。
 * 档位默认值（sm 28/11/6 · md 38/14/8 · lg 52/18/10、默认 md）与各调用方 1:1 保真。
 */

import type { CSSProperties } from 'react';

/** 单个尺寸档位：box=方块边长，font=字号，radius=圆角 */
export interface AppIconSize {
  box: number;
  font: number;
  radius: number;
}

/** sm/md/lg 三档尺寸（逐字搬蓝本） */
export const APP_ICON_SIZES: Record<'sm' | 'md' | 'lg', AppIconSize> = {
  sm: { box: 28, font: 11, radius: 6 },
  md: { box: 38, font: 14, radius: 8 },
  lg: { box: 52, font: 18, radius: 10 },
};

interface AppIconProps {
  /** App 对象（提供 color / icon 兜底来源） */
  app?: { color?: string; icon?: string } | null;
  /** 显式背景色，优先于 app.color */
  color?: string;
  /** 显式占位字，优先于 app.icon */
  label?: string;
  /** 尺寸：档位名 sm/md/lg（默认 md）或 {box,font,radius} 自定义对象 */
  size?: 'sm' | 'md' | 'lg' | AppIconSize;
  /** 透传外层样式覆盖 */
  style?: CSSProperties;
}

export function AppIcon({ app, color, label, size = 'md', style }: AppIconProps) {
  const dim = typeof size === 'object' ? size : APP_ICON_SIZES[size] || APP_ICON_SIZES.md;
  const bg = color || app?.color || 'var(--accent)';
  const txt = label ?? app?.icon ?? '';
  return (
    <div
      className="app-icon"
      style={{
        width: dim.box,
        height: dim.box,
        fontSize: dim.font,
        background: bg,
        borderRadius: dim.radius,
        ...style,
      }}
    >
      {txt}
    </div>
  );
}
